import React from 'react';
import Stack from '@mui/material/Stack';
import { useFiltersRow } from '../components/FiltersRow';
import SaveAsDialog from '../components/SaveAsDialog';
import ColumnPicker from '../components/ReportsTable/ColumnPicker';
import { useProductsColumns } from '../hooks/useProductsColumns';
import { QUICK_FILTER_LABELS } from '../constants/quickFilters';
import ProductsLeftPanel from './ProductsLeftPanel';
import PageHeader from './components/PageHeader';
import ViewContent from './components/ViewContent';
import ViewTabs from './components/ViewTabs';
import {
    ALL_PRODUCTS_FILTER_FIELDS,
    resolveFilter,
    rowsEqual,
    quickFilterToRows,
} from './utils/filterLogic';
import { FILTER_PARAM, VIEW_PARAM, VIEW_TABS_VISIBLE } from './utils/viewConfig';

/**
 * Inner page — rendered only after user + savedFilters have resolved.
 * baselineRows drives useProductsFilters reset on filter switch.
 *
 * Props:
 *   user, reports, reportsLoading, maxFilterNameLength
 *   savedFilters, onSaveAsFilter, updateFilter, renameFilter, removeFilter, reorderFilters
 *   searchParams, setSearchParams
 */
const AllProductsPageInner = React.memo(function AllProductsPageInner({
    user, reports, reportsLoading, onReportsRefresh, maxFilterNameLength, savedFilters,
    onSaveAsFilter, updateFilter, renameFilter, removeFilter, reorderFilters,
    searchParams, setSearchParams, viewerMode,
}) {
    const [saveAsOpen, setSaveAsOpen] = React.useState(false);

    // Column visibility is owned here so the picker can sit in the tab row while the
    // resolved columns still flow down to the table in ViewContent.
    const { columns, visibleFields, toggleField } = useProductsColumns(user?.uid);

    // useTransition for non-urgent mutations (save/update/rename/reorder)
    const [isPending, startTransition] = React.useTransition();

    const setFilterParam = React.useCallback((value) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            if (value) next.set(FILTER_PARAM, value); else next.delete(FILTER_PARAM);
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    const activeView = searchParams.get(VIEW_PARAM) ?? 'list';

    const setView = React.useCallback((_, value) => {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            value === 'list' ? next.delete(VIEW_PARAM) : next.set(VIEW_PARAM, value);
            return next;
        }, { replace: true });
    }, [setSearchParams]);

    // activeQuickFilter is fully derived from URL + savedFilters — no useState, no desync.
    const activeQuickFilter = React.useMemo(() => {
        const param = searchParams.get(FILTER_PARAM);
        return resolveFilter(param, savedFilters, user?.displayName).activeQuickFilter;
    }, [searchParams, savedFilters, user?.displayName]);

    const activeSavedFilterId = activeQuickFilter?.startsWith('saved:')
        ? activeQuickFilter.slice(6) : null;

    const activeSavedFilter = activeSavedFilterId
        ? savedFilters.find((f) => f.id === activeSavedFilterId)
        : null;

    // baselineRows changes identity only when the active filter changes — drives rows reset.
    const baselineRows = React.useMemo(() => {
        if (activeSavedFilterId) {
            return savedFilters.find((f) => f.id === activeSavedFilterId)?.rows ?? [];
        }
        return quickFilterToRows(activeQuickFilter, user?.displayName);
    }, [activeSavedFilterId, activeQuickFilter, savedFilters, user?.displayName]);

    // For built-in quick filters the Product Type is determined by the view itself and must
    // not be changed by the user. Saved filters have no locked fields.
    const lockedKeys = React.useMemo(
        () => (activeSavedFilterId ? [] : ['productType.label']),
        [activeSavedFilterId],
    );

    const { filteredReports, filtersBar, filterRows } = useFiltersRow(
        reports,
        ALL_PRODUCTS_FILTER_FIELDS,
        { baselineRows, lockedKeys },
    );

    const pageTitle = activeSavedFilter?.name
        ?? QUICK_FILTER_LABELS[activeQuickFilter]
        ?? 'All Products';

    const isDirty = React.useMemo(
        () => !rowsEqual(filterRows, baselineRows),
        [filterRows, baselineRows],
    );

    const handleQuickFilter = React.useCallback((key) => {
        setFilterParam(key);
    }, [setFilterParam]);

    const handleApplySavedFilter = React.useCallback((sf) => {
        setFilterParam(`saved:${sf.id}`);
    }, [setFilterParam]);

    const handleUpdateSavedFilter = React.useCallback(() => {
        if (!activeSavedFilterId) return;
        startTransition(() => {
            updateFilter(activeSavedFilterId, filterRows);
        });
    }, [activeSavedFilterId, filterRows, updateFilter]);

    /**
     * Save As: synchronous — onSaveAsFilter (outer shell) is optimistic and returns the
     * new entry immediately. Calling it outside startTransition ensures the URL update
     * (setFilterParam inside onSaveAsFilter) commits in the same synchronous event.
     */
    const handleSaveAsConfirm = React.useCallback((name) => {
        setSaveAsOpen(false);
        onSaveAsFilter({ name, rows: filterRows });
    }, [onSaveAsFilter, filterRows]);

    const handleRenameFilterFromPanel = React.useCallback((id, name) => {
        startTransition(() => {
            renameFilter(id, name);
        });
    }, [renameFilter]);

    const handleReorderFilters = React.useCallback((newOrderedIds) => {
        startTransition(() => {
            reorderFilters(newOrderedIds);
        });
    }, [reorderFilters]);

    const handleDeleteFromHeader = React.useCallback(() => {
        removeFilter(activeSavedFilterId);
        setFilterParam(null);
    }, [removeFilter, activeSavedFilterId, setFilterParam]);

    const handleRenameFromHeader = React.useCallback((id, name) => {
        startTransition(() => {
            renameFilter(id, name);
        });
    }, [renameFilter]);

    return (
        <Stack direction="row" sx={{ flex: 1, overflow: 'hidden', height: '100%' }}>
            {!viewerMode && (
                <ProductsLeftPanel
                    activeQuickFilter={activeQuickFilter}
                    onQuickFilter={handleQuickFilter}
                    savedFilters={savedFilters}
                    maxFilterNameLength={maxFilterNameLength}
                    onApplySavedFilter={handleApplySavedFilter}
                    onNewFilter={() => setSaveAsOpen(true)}
                    onRenameFilter={handleRenameFilterFromPanel}
                    onDeleteSavedFilter={removeFilter}
                    onReorderFilters={handleReorderFilters}
                />
            )}

            <Stack sx={{ flex: 1, overflow: 'auto' }}>
                {/* PageHeader owns its internal p={2} spacing={1} wrapper */}
                <PageHeader
                    pageTitle={pageTitle}
                    isDirty={isDirty}
                    activeSavedFilterId={activeSavedFilterId}
                    activeSavedFilter={activeSavedFilter}
                    isPending={isPending}
                    user={user}
                    filtersBar={filtersBar}
                    maxFilterNameLength={maxFilterNameLength}
                    onUpdateSavedFilter={handleUpdateSavedFilter}
                    onOpenSaveAs={() => setSaveAsOpen(true)}
                    onDeleteFilter={handleDeleteFromHeader}
                    renameFilter={handleRenameFromHeader}
                    viewerMode={viewerMode}
                />

                <ViewTabs
                    activeView={activeView}
                    onChange={setView}
                    actions={(!VIEW_TABS_VISIBLE || activeView === 'list')
                        ? <ColumnPicker visibleFields={visibleFields} onToggleField={toggleField} />
                        : null}
                />

                <ViewContent
                    activeView={activeView}
                    filteredReports={filteredReports}
                    loading={reportsLoading}
                    columns={columns}
                    onReportsRefresh={onReportsRefresh}
                />
            </Stack>

            {/* Save As dialog — opened when isDirty and no active saved filter, or from left panel */}
            <SaveAsDialog
                open={saveAsOpen}
                onClose={() => setSaveAsOpen(false)}
                onConfirm={handleSaveAsConfirm}
                maxLength={maxFilterNameLength}
            />
        </Stack>
    );
});

export default AllProductsPageInner;
